import React, { useState } from 'react';
import { FiEdit, FiPlus } from 'react-icons/fi';
import AddTaxModal from './AddTaxModal';

interface TaxRate {
  id: string;
  region: string;
  rate: string;
  effectiveDate: string;
}

const formatDate = (dateStr: string) => {
  const [year, month, day] = dateStr.split("-");
  return `${day}/${month}/${year}`;
};


const TaxSettings: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [taxRates, setTaxRates] = useState<TaxRate[]>([
    {
      id: Math.random().toString(36).substring(2, 9),
      region: 'Saudi Arabia',
      rate: '5%',
      effectiveDate: '2024-05-25',
    },
    {
      id: Math.random().toString(36).substring(2, 9),
      region: 'US',
      rate: '7.5%',
      effectiveDate: '2024-05-25',
    },
  ]);

  const [mode, setMode] = useState<'add' | 'edit'>('add');
  const [selectedTax, setSelectedTax] = useState<TaxRate | null>(null);

  const totalItems = taxRates.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const pages = Array.from({ length: totalPages }, (_, i) => i + 1);

  const handleAddTax = (newTax: TaxRate) => {
    if (mode === 'add') {
      setTaxRates(prev => [
        ...prev,
        {
          ...newTax,
         
        },
      ]);
    } else if (mode === 'edit' && selectedTax) {
      setTaxRates(prev =>
        prev.map(tax =>
          tax.id === selectedTax.id
            ? {
                ...newTax,
                id: selectedTax.id,
               
              }
            : tax
        )
      );
    }
    setIsAddModalOpen(false);
    setSelectedTax(null);
    setMode('add');
  };

  const handleEditTax = (id: string) => {
    const tax = taxRates.find(t => t.id === id);
    if (tax) {
      setSelectedTax({
        ...tax,
        rate: tax.rate.replace('%', ''),
        effectiveDate: new Date(tax.effectiveDate).toISOString().split('T')[0], // for input[type="date"]
      });
      setMode('edit');
      setIsAddModalOpen(true);
    }
  };

  return (
    <div className="mt-10">
      <div className="flex justify-between items-center my-5">
        <h2 className="text-lg font-semibold text-white">Tax rates</h2>
        <button
          onClick={() => {
            setSelectedTax(null);
            setMode('add');
            setIsAddModalOpen(true);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-[#2A2A2A] text-white rounded-lg hover:bg-[#3a3a3a33] transition-colors"
        >
          <FiPlus size={16} />
          Add Tax
        </button>
      </div>

      <div className="bg-[#2A2A2A] rounded-lg overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-[#1A1A1A] text-gray-400 text-sm">
              <th className="text-left p-4 font-medium">Region</th>
              <th className="text-left p-4 font-medium">Rate</th>
              <th className="text-left p-4 font-medium">Effective date</th>
              <th className="text-right p-4 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {taxRates.map((tax) => (
              <tr key={tax.id} className="border-b border-white/[0.08] hover:bg-[#3a3a3a33]">
                <td className="p-4 text-gray-300">{tax.region}</td>
                <td className="p-4 text-gray-300">{tax.rate}</td>
                <td className="p-4 text-gray-300">{formatDate(tax.effectiveDate)}</td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex justify-end gap-3">
                    <button
                      onClick={() => handleEditTax(tax.id)}
                      className="text-blue-500 hover:text-blue-400"
                    >
                      <FiEdit size={18} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between mt-4 text-sm text-gray-400">
        <div>
          Showing {(currentPage - 1) * itemsPerPage + 1}-
          {Math.min(currentPage * itemsPerPage, totalItems)} from {totalItems}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="p-2 hover:text-white disabled:opacity-50"
          >
            ←
          </button>
          {pages.map(page => (
            <button
              key={page}
              onClick={() => setCurrentPage(page)}
              className={`w-8 h-8 rounded flex items-center justify-center ${
                currentPage === page ? 'bg-[#3A3A3A] text-white' : 'hover:text-white'
              }`}
            >
              {page}
            </button>
          ))}
          <button
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className="p-2 hover:text-white disabled:opacity-50"
          >
            →
          </button>
        </div>
      </div>

      <AddTaxModal
        isOpen={isAddModalOpen}
        onClose={() => {
          setIsAddModalOpen(false);
          setSelectedTax(null);
          setMode('add');
        }}
        onSave={handleAddTax}
        defaultValues={selectedTax}
        mode={mode}
      />
    </div>
  );
};

export default TaxSettings;
